import * as React from 'react';
import { useRef } from 'react';

import { captureException } from '@sentry/browser';
import { translate } from '~l10n/client';

import { CreateWorkspaceEventAdapter } from '~analytics/adapters/CreateWorkspaceEventAdapter';
import { SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import { Stack } from '~shared/components/Stack';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { navigateToWorkspace } from '~teacher/components/workspaces/WorkspaceQuizzes/utils/navigateByItemType/navigateToWorkspace';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useCreateWorkspace } from '~teacher/hook/workspaces';

import { isAxiosError } from '../../../../api/isAxiosError';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface UseResetCreateWorkspaceDialogParams {
  isOpen: boolean;
  setIsDisabled: (value: ((prevState: boolean) => boolean) | boolean) => void;
  setWorkspaceName: (value: ((prevState: string) => string) | string) => void;
}

function useResetCreateWorkspaceDialog({
  isOpen,
  setIsDisabled,
  setWorkspaceName,
}: UseResetCreateWorkspaceDialogParams) {
  React.useEffect(() => {
    if (isOpen) {
      setIsDisabled(false);
      setWorkspaceName(translate('New Library'));
    }
  }, [isOpen, setWorkspaceName, setIsDisabled]);
}

interface UseHandleCreateWorkspaceParams {
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  workspaceName: string;
  onClose: () => void;
}

function useHandleCreateWorkspace({
  setIsDisabled,
  workspaceName,
  onClose,
}: UseHandleCreateWorkspaceParams) {
  const applicationContext = useTeacherApplicationContext();
  const createWorkspaceMutation = useCreateWorkspace();
  const router = useSocrativeRouter();
  return () => {
    setIsDisabled(true);
    createWorkspaceMutation.mutate(
      {
        workspaceName,
        isAssessmentsLibrary:
          applicationContext === TeacherApplicationContext.ShowbieAssessments,
      },
      {
        onSuccess: ({ id: workspaceId }) => {
          new CreateWorkspaceEventAdapter({
            workspace_id: workspaceId,
          }).sendAnalytics();
          onClose();
          navigateToWorkspace({
            router: router,
            workspaceId: workspaceId,
          });
        },
        onSettled: () => {
          setIsDisabled(false);
        },
        onError: (error) => {
          if (isAxiosError(error)) {
            // 400-500 errors are all axios errors.
            if (error.response.status === 422) {
              toast.critical(
                translate(
                  `You've reached the maximum number of Shared Libraries`
                )
              );
            } else if (error.response.status >= 500) {
              captureException(error);
            } else {
              toast.critical(
                translate(
                  'An error occurred while trying to create the shared library.'
                )
              );
            }
          } else {
            captureException(error);
            toast.critical(
              translate(
                'An error occurred while trying to create the shared library.'
              )
            );
          }
        },
      }
    );
  };
}

export function CreateWorkspaceDialog({ isOpen, onClose }: Props) {
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [workspaceName, setWorkspaceName] = React.useState<string>(
    translate('New Library')
  );

  const formRef = useRef<HTMLFormElement>(null);

  const onWorkspaceNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setWorkspaceName(event.target.value);

  const autoFocusOnOpenCallback = useAutoFocus();

  const handleCreateWorkspace = useHandleCreateWorkspace({
    setIsDisabled: setIsDisabled,
    workspaceName: workspaceName,
    onClose: onClose,
  });

  useResetCreateWorkspaceDialog({
    isOpen: isOpen,
    setIsDisabled: setIsDisabled,
    setWorkspaceName: setWorkspaceName,
  });

  return (
    <Dialog
      id="create-workspace-dialog"
      open={isOpen}
      onClose={onClose}
      title={translate('Create Library')}
      closeLabel={translate('Close')}
    >
      <form
        ref={formRef}
        onSubmit={(event) => {
          event.preventDefault();
          if (formRef.current.checkValidity()) {
            handleCreateWorkspace();
          }
        }}
      >
        <div className="pt-4 text-base leading-8 text-blue-900">
          <Stack space="8">
            <Stack space="4">
              <Divider />
              <div className="text-base text-blue-600">
                {translate('Create, edit, and share quizzes with your peers')}
              </div>
              <Divider />
            </Stack>
            <Stack space="4">
              <label htmlFor="library-name">{translate('Library Name')}</label>
              <div className="flex gap-6">
                <TextInput
                  ref={autoFocusOnOpenCallback}
                  maxLength={128}
                  minLength={3}
                  required
                  id="library-name"
                  defaultValue={workspaceName}
                  onChange={onWorkspaceNameChange}
                  placeholder={translate('Shared Library name')}
                  className="flex-1 min-w-40 focus:bg-grey-50"
                />
                <SmallButton
                  type="submit"
                  isDisabled={isDisabled}
                  appearance="primary"
                  className="whitespace-nowrap"
                  data-testid="create-library-button"
                >
                  {translate('Create Library')}
                </SmallButton>
              </div>
            </Stack>
            <div className="text-base text-blue-600">
              {translate(
                'You will be the Owner of this library. After creating the library, you can invite members to view and edit content.'
              )}
            </div>
          </Stack>
        </div>
      </form>
    </Dialog>
  );
}
